// CollaborationDetailModal — Rich detail popup for collaboration items
// Shows full biographical data, org associations, journal links, and "Why suggested?" explanations
import { useState, useEffect } from 'react'
import { X, User, Building2, Link2, Sparkles, CheckSquare, BookOpen, Users, Award, ExternalLink, Loader2 } from 'lucide-react'
import type { CollaborationContact, CollaborationOrg, CollaborationLink, CollaborationTodo, DetailItem } from '../../types/collaboration'
import type { CrmContactOrg, DirectoryEntity, DirectoryPublication, DirectoryCoAuthor } from '../../types'
import { getDirectoryEntity, getDirectoryPublications, getDirectoryCoAuthors } from '../../services/api'
import { openAlexUrl } from '../../utils/externalLinks'

interface CollaborationDetailModalProps {
  detail: DetailItem | null
  onClose: () => void
  /** All contacts for cross-referencing org detail */
  allContacts?: CollaborationContact[]
  /** All orgs for cross-referencing contact detail */
  allOrgs?: CollaborationOrg[]
  /** All links for cross-referencing contact/org detail */
  allLinks?: CollaborationLink[]
  /** Contact-org link records */
  contactOrgLinks?: CrmContactOrg[]
}

function FieldRow({ label, value }: { label: string; value?: string | number | null }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div className="flex gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs font-medium text-gray-500 w-32 shrink-0">{label}</span>
      <span className="text-sm text-gray-900 break-words">{value}</span>
    </div>
  )
}

function SectionHeader({ icon: Icon, label }: { icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <div className="flex items-center gap-1.5 mt-4 mb-2">
      <Icon className="w-3.5 h-3.5 text-gray-500" />
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
  )
}

function SourceBadge({ source, notes }: { source?: string; notes?: string }) {
  const raw = source || notes || ''
  const isAI = raw.includes('ai_directory_match') || raw.includes('ai_suggested') || raw.startsWith('source: ai_')
  const isEcosystem = raw.includes('ai_ecosystem_match')
  const isDirectory = raw.includes('directory-copy') || raw.includes('directory')
  const label = isEcosystem ? 'Ecosystem Match' : isAI ? 'AI Directory Match' : isDirectory ? 'From Directory' : 'Manual'
  const color = isEcosystem ? 'bg-sky-50 text-sky-700 border-sky-200'
    : isAI ? 'bg-purple-50 text-purple-700 border-purple-200'
    : isDirectory ? 'bg-teal-50 text-teal-700 border-teal-200'
    : 'bg-gray-50 text-gray-600 border-gray-200'
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${color}`}>{label}</span>
}

function WhyExplanation({ text }: { text: string }) {
  return (
    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <p className="text-xs font-semibold text-blue-700 mb-1">Why was this suggested?</p>
      <p className="text-sm text-blue-900 leading-relaxed">{text}</p>
    </div>
  )
}

function TopicBadge({ topic }: { topic: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-200">
      {topic}
    </span>
  )
}

// ============================================================================
// Contact Detail — enriched with directory profile, org links, journal links
// ============================================================================

function ContactDetail({ item, allOrgs, allLinks, contactOrgLinks }: {
  item: CollaborationContact
  allOrgs?: CollaborationOrg[]
  allLinks?: CollaborationLink[]
  contactOrgLinks?: CrmContactOrg[]
}) {
  const [profile, setProfile] = useState<DirectoryEntity | null>(null)
  const [publications, setPublications] = useState<DirectoryPublication[]>([])
  const [coAuthors, setCoAuthors] = useState<DirectoryCoAuthor[]>([])
  const [loading, setLoading] = useState(false)

  // The entity_id for directory lookup: centralDirectoryId or resource_id from notes
  const entityId = item.centralDirectoryId || ''

  useEffect(() => {
    if (!entityId) return
    setLoading(true)
    Promise.allSettled([
      getDirectoryEntity(entityId),
      getDirectoryPublications(entityId, 1, 5),
      getDirectoryCoAuthors(entityId),
    ]).then(([entityRes, pubRes, coRes]) => {
      if (entityRes.status === 'fulfilled') setProfile(entityRes.value)
      if (pubRes.status === 'fulfilled') setPublications(pubRes.value.publications || [])
      if (coRes.status === 'fulfilled') setCoAuthors(coRes.value.co_authors?.slice(0, 5) || [])
    }).finally(() => setLoading(false))
  }, [entityId])

  // Cross-reference: which orgs does this contact belong to?
  const linkedOrgs = (contactOrgLinks || [])
    .filter(link => link.contact_id === item.id)
    .map(link => (allOrgs || []).find(o => o.id === link.org_id))
    .filter(Boolean) as CollaborationOrg[]

  // Also check item.orgName if no cross-ref found
  const orgDisplay = linkedOrgs.length > 0
    ? linkedOrgs.map(o => o.name).join(', ')
    : item.orgName || profile?.affiliation_name || ''

  // Cross-reference: which journals/patents reference this contact?
  const linkedLinks = (allLinks || []).filter(l => l.contactIds.includes(item.id))
  const linkedJournals = linkedLinks.filter(l => l.type === 'journal')
  const linkedPatents = linkedLinks.filter(l => l.type === 'patent')

  const isDirectoryMatch = item.notes?.includes('ai_directory_match') || !!item.centralDirectoryId
  const isEcosystem = item.source === 'ai_ecosystem_match'

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-iflow-100 flex items-center justify-center">
          <User className="w-6 h-6 text-iflow-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
          {orgDisplay && <p className="text-sm text-gray-500">{orgDisplay}</p>}
          <div className="flex gap-1.5 mt-1">
            <SourceBadge source={item.source} notes={item.notes} />
          </div>
        </div>
      </div>

      {/* Basic fields */}
      <div className="space-y-0.5">
        <FieldRow label="Role" value={item.role} />
        <FieldRow label="Email" value={item.email} />
        <FieldRow label="Organisation" value={orgDisplay} />
        {profile?.entity_type && <FieldRow label="Type" value={profile.entity_type} />}
        {profile?.country_code && <FieldRow label="Country" value={profile.country_code} />}
        {(profile?.city || profile?.state) && <FieldRow label="Location" value={[profile.city, profile.state].filter(Boolean).join(', ')} />}
      </div>

      {/* Directory profile — biographical metrics */}
      {loading && (
        <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading full profile...
        </div>
      )}

      {profile && (
        <>
          <SectionHeader icon={Award} label="Research Metrics" />
          <div className="grid grid-cols-3 gap-2">
            {profile.h_index != null && (
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{profile.h_index}</p>
                <p className="text-[10px] text-gray-500 uppercase">h-index</p>
              </div>
            )}
            {(profile.paper_count ?? profile.works_count) != null && (
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{profile.paper_count ?? profile.works_count}</p>
                <p className="text-[10px] text-gray-500 uppercase">Publications</p>
              </div>
            )}
            {(profile.citation_count ?? profile.cited_by_count) != null && (
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{(profile.citation_count ?? profile.cited_by_count)?.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500 uppercase">Citations</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Topics / research interests */}
      {profile?.topics && profile.topics.length > 0 && (
        <>
          <SectionHeader icon={BookOpen} label="Research Topics" />
          <div className="flex flex-wrap gap-1.5">
            {profile.topics.slice(0, 8).map((topic, i) => (
              <TopicBadge key={i} topic={topic} />
            ))}
          </div>
        </>
      )}

      {/* Linked organisations */}
      {linkedOrgs.length > 0 && (
        <>
          <SectionHeader icon={Building2} label="Organisations" />
          <div className="space-y-1">
            {linkedOrgs.map(org => (
              <div key={org.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg text-sm">
                <Building2 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="text-gray-900">{org.name}</span>
                {org.industry && <span className="text-gray-500 text-xs">({org.industry})</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Associated journals/patents */}
      {linkedJournals.length > 0 && (
        <>
          <SectionHeader icon={BookOpen} label={`Associated Journals (${linkedJournals.length})`} />
          <div className="space-y-1">
            {linkedJournals.slice(0, 5).map(j => (
              <div key={j.id} className="flex items-center gap-2 px-2 py-1.5 bg-purple-50/50 rounded-lg text-sm">
                <BookOpen className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                <span className="text-gray-900 truncate">{j.title}</span>
                {j.similarityScore != null && (
                  <span className="text-xs text-purple-600 shrink-0">{j.similarityScore}%</span>
                )}
              </div>
            ))}
            {linkedJournals.length > 5 && (
              <p className="text-xs text-gray-500 pl-2">+{linkedJournals.length - 5} more</p>
            )}
          </div>
        </>
      )}

      {linkedPatents.length > 0 && (
        <>
          <SectionHeader icon={Link2} label={`Associated Patents (${linkedPatents.length})`} />
          <div className="space-y-1">
            {linkedPatents.slice(0, 3).map(p => (
              <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 bg-amber-50/50 rounded-lg text-sm">
                <Link2 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="text-gray-900 truncate">{p.title}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Co-authors */}
      {coAuthors.length > 0 && (
        <>
          <SectionHeader icon={Users} label={`Co-Author Network (${coAuthors.length})`} />
          <div className="space-y-1">
            {coAuthors.map((ca, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg text-sm">
                <User className="w-3.5 h-3.5 text-iflow-500 shrink-0" />
                <div className="min-w-0">
                  <span className="text-gray-900">{ca.name || ca.entity_id.slice(0, 8)}</span>
                  {ca.affiliation && <span className="text-xs text-gray-500 ml-1">({ca.affiliation})</span>}
                </div>
                {ca.paper_count != null && ca.paper_count > 0 && (
                  <span className="text-xs text-gray-500 shrink-0 ml-auto">{ca.paper_count} shared</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Recent publications */}
      {publications.length > 0 && (
        <>
          <SectionHeader icon={BookOpen} label="Recent Publications" />
          <div className="space-y-1.5">
            {publications.map((pub, i) => (
              <div key={i} className="px-2 py-1.5 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900 leading-snug">{pub.title}</p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                  {pub.year && <span>{pub.year}</span>}
                  {pub.journal && <span>{pub.journal}</span>}
                  {pub.citation_count != null && <span>{pub.citation_count} citations</span>}
                  {pub.doi && (
                    <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer" className="text-iflow-500 hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                      DOI <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* External links */}
      {profile?.openalex_id && (
        <div className="mt-3">
          <a href={openAlexUrl(profile.openalex_id)} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-iflow-600 bg-iflow-50 rounded-lg hover:bg-iflow-100"
            onClick={e => e.stopPropagation()}>
            <ExternalLink className="w-3.5 h-3.5" /> View on OpenAlex
          </a>
        </div>
      )}

      {/* Why was this suggested? */}
      {isDirectoryMatch && (
        <WhyExplanation text={`This contact was identified by AI-powered directory matching. The system compared this document's content embedding against 1.1 million researchers and institutions in the collaborator directory using cosine similarity search. ${item.name} was matched because their published research topics and institutional profile are semantically similar to the content of your document.${profile ? ` They have an h-index of ${profile.h_index || 'N/A'}, ${profile.works_count || profile.paper_count || 0} publications, and ${(profile.cited_by_count || profile.citation_count || 0).toLocaleString()} citations.` : ''}`} />
      )}
      {isEcosystem && (
        <WhyExplanation text={`This contact was identified through ecosystem profile matching. The system matched this document against profiles of investors, IP attorneys, accelerators, and manufacturers to find relevant ecosystem participants who could support commercialisation of this research.`} />
      )}
      {!isDirectoryMatch && !isEcosystem && item.notes && !item.notes.startsWith('source:') && (
        <WhyExplanation text={item.notes} />
      )}
    </>
  )
}

// ============================================================================
// Organisation Detail
// ============================================================================

function OrgDetail({ item, allContacts, allLinks, contactOrgLinks }: {
  item: CollaborationOrg
  allContacts?: CollaborationContact[]
  allLinks?: CollaborationLink[]
  contactOrgLinks?: CrmContactOrg[]
}) {
  const isDirectoryMatch = item.notes?.includes('ai_directory_match') || !!item.centralDirectoryId

  // Cross-reference: which contacts belong to this org?
  const linkedContacts = (contactOrgLinks || [])
    .filter(link => link.org_id === item.id)
    .map(link => (allContacts || []).find(c => c.id === link.contact_id))
    .filter(Boolean) as CollaborationContact[]

  // Cross-reference: which links reference this org?
  const linkedLinks = (allLinks || []).filter(l => l.orgIds.includes(item.id))

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
          {item.industry && <p className="text-sm text-gray-500">{item.industry}</p>}
          <div className="flex gap-1.5 mt-1">
            <SourceBadge source={item.source} notes={item.notes} />
          </div>
        </div>
      </div>
      <div className="space-y-0.5">
        <FieldRow label="Industry" value={item.industry} />
        <FieldRow label="Total Contacts" value={item.contactCount > 0 ? `${item.contactCount} linked` : undefined} />
      </div>

      {linkedContacts.length > 0 && (
        <>
          <SectionHeader icon={Users} label={`Members (${linkedContacts.length})`} />
          <div className="space-y-1">
            {linkedContacts.map(c => (
              <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg text-sm">
                <User className="w-3.5 h-3.5 text-iflow-500 shrink-0" />
                <div className="min-w-0">
                  <span className="text-gray-900">{c.name}</span>
                  {c.role && <span className="text-xs text-gray-500 ml-1">({c.role})</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {linkedLinks.length > 0 && (
        <>
          <SectionHeader icon={Link2} label={`Associated Links (${linkedLinks.length})`} />
          <div className="space-y-1">
            {linkedLinks.slice(0, 5).map(l => (
              <div key={l.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg text-sm">
                <BookOpen className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                <span className="text-gray-900 truncate">{l.title}</span>
                <span className="text-xs text-gray-500 shrink-0">{l.type}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {isDirectoryMatch && (
        <WhyExplanation text="This organisation was identified by AI-powered directory matching. The system compared this document's content against institutional profiles of universities, research labs, and companies in the collaborator directory to find organisations whose research output is semantically similar to your document." />
      )}
    </>
  )
}

// ============================================================================
// Link Detail
// ============================================================================

function LinkDetail({ item }: { item: CollaborationLink }) {
  const isAI = item.notes?.startsWith('source: ai_')
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${item.type === 'journal' ? 'bg-purple-100' : 'bg-amber-100'}`}>
          {item.type === 'journal' ? <BookOpen className="w-6 h-6 text-purple-600" /> : <Link2 className="w-6 h-6 text-amber-600" />}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
          <p className="text-sm text-gray-500 capitalize">{item.type}</p>
          {isAI && (
            <div className="mt-1">
              <SourceBadge source={item.notes} />
            </div>
          )}
        </div>
      </div>
      <div className="space-y-0.5">
        <FieldRow label="Type" value={item.type === 'journal' ? 'Academic Journal / Paper' : 'Patent'} />
        {item.doi && <FieldRow label="DOI" value={item.doi} />}
        {item.filing && <FieldRow label="Filing Number" value={item.filing} />}
        {item.similarityScore != null && <FieldRow label="Similarity Score" value={`${item.similarityScore}% match to your document`} />}
      </div>
      {item.doi && (
        <a href={`https://doi.org/${item.doi}`} target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-iflow-600 bg-iflow-50 rounded-lg hover:bg-iflow-100"
          onClick={e => e.stopPropagation()}>
          <ExternalLink className="w-3.5 h-3.5" /> View Publication
        </a>
      )}
      {isAI && item.type === 'journal' && (
        <WhyExplanation text={`This journal article was identified by AI-powered academic matching. The system compared your document's content embedding against a database of academic publications using cosine similarity. This paper scored ${item.similarityScore != null ? `${item.similarityScore}%` : 'highly'} in semantic similarity, meaning its content and research themes closely align with your document.`} />
      )}
      {isAI && item.type === 'patent' && (
        <WhyExplanation text="This patent was identified by AI-powered prior art analysis. The system compared your document's content against patent databases to find relevant intellectual property that may relate to your research." />
      )}
    </>
  )
}

// ============================================================================
// Todo / Suggestion Detail
// ============================================================================

function TodoDetail({ item }: { item: CollaborationTodo }) {
  const isSuggestion = item.status === 'suggested'
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isSuggestion ? 'bg-purple-100' : 'bg-green-100'}`}>
          {isSuggestion ? <Sparkles className="w-6 h-6 text-purple-600" /> : <CheckSquare className="w-6 h-6 text-green-600" />}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
          <p className="text-sm text-gray-500 capitalize">{item.status.replace('_', ' ')} &middot; {item.priority} priority</p>
        </div>
      </div>
      <div className="space-y-0.5">
        <FieldRow label="Status" value={item.status.replace('_', ' ')} />
        <FieldRow label="Priority" value={item.priority} />
        {item.dueDate && <FieldRow label="Due Date" value={item.dueDate} />}
      </div>
      {item.description && <WhyExplanation text={item.description} />}
      {isSuggestion && !item.description && (
        <WhyExplanation text="This action was suggested by the WhatNext AI engine, which analyses your documents and identifies actionable next steps based on document content, collaboration opportunities, and research gaps." />
      )}
    </>
  )
}

// ============================================================================
// Modal Shell
// ============================================================================

export default function CollaborationDetailModal({
  detail, onClose, allContacts, allOrgs, allLinks, contactOrgLinks,
}: CollaborationDetailModalProps) {
  if (!detail) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white rounded-t-xl flex items-center justify-between px-5 py-3 border-b border-gray-200 z-10">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {detail.type === 'contact' ? 'Contact Details' : detail.type === 'org' ? 'Organisation Details' : detail.type === 'link' ? 'Link Details' : 'Action Details'}
          </span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-5">
          {detail.type === 'contact' && (
            <ContactDetail item={detail.item} allOrgs={allOrgs} allLinks={allLinks} contactOrgLinks={contactOrgLinks} />
          )}
          {detail.type === 'org' && (
            <OrgDetail item={detail.item} allContacts={allContacts} allLinks={allLinks} contactOrgLinks={contactOrgLinks} />
          )}
          {detail.type === 'link' && <LinkDetail item={detail.item} />}
          {detail.type === 'todo' && <TodoDetail item={detail.item} />}
        </div>
      </div>
    </div>
  )
}
